import * as Sentry from '@sentry/react';

import {
  LocationProvider,
  Redirect,
  RouteComponentProps,
  useLocation,
} from '@gatsbyjs/reach-router';
import React from 'react';
import { createRoot } from 'react-dom/client';
import DHRouter from '../components/atom/DHRouter';
import AccountSelectionWrapper from '../components/page/AccountSelectionWrapper';
import Root from '~/Root';
import Auth from '~/Root/Auth';
import ForgotPassword from '../components/page/Auth/ForgotPassword/index';
import Login from '../components/page/Auth/Login';
import Register from '../components/page/Auth/Register';
import Verify from '../components/page/Auth/Verify/index';
import SetupUserDetails from '../components/page/Auth/SetupUserDetails';
import Logout from '../components/page/Auth/Logout';
import SetupNewPassword from '../components/page/Auth/SetupNewPassword';
import AuthWrapperHOC from '../components/page/Auth/components/AuthWrapperHOC';
import BndlLoading from '../components/atom/BndlLoading';
import { configureAmplify } from '~/amplify';
import usePageTracking from '~/hooks/usePageTracking';
import { SIGNUP_ID } from '~/util/localStorageKeys';
import useStoreUrlParam from '~/hooks/useStoreUrlParam';

// Configure Amplify at app startup
void configureAmplify();

const Crocs: React.FCC<RouteComponentProps> = () => (
  <Root>
    <InsideRootContent />
  </Root>
);

const InsideRootContent: React.FC = () => {
  // Track when recoil state is available
  usePageTracking();

  useStoreUrlParam({
    paramName: 'signupId',
    storageKey: SIGNUP_ID,
  });

  return (
    <Auth>
      <AuthWrapperHOC>
        <DHRouter basepath="/" height="auto" primary>
          <Login path="login" />
          <Logout path="logout" />
          <ForgotPassword path="forgot-password" />
          <Register path="register" />
          <SetupNewPassword path="setup-newpassword" />
          <SetupUserDetails path="setup-user-details" />
          <Verify path="verify" />
          <AccountSelectionWrapper path="/-/*" />
          <Redirector default />
        </DHRouter>
      </AuthWrapperHOC>
    </Auth>
  );
};

const Redirector: React.FC<{ default?: boolean }> = () => {
  const location = useLocation();
  return (
    <>
      <BndlLoading />
      <Redirect default noThrow from="/*" to={`/-${location.pathname}`} />
    </>
  );
};

Sentry.init({
  dsn: 'https://9ac9ae134dcd45f7b61c5cffdef618c2@o1029640.ingest.sentry.io/5996656',
  environment:
    process.env.STAGE_NAME === 'prod'
      ? 'production'
      : process.env.STAGE_NAME || 'dev',
  release: process.env.APP_VERSION || 'n/a',
  denyUrls: ['localhost'],
  enabled: process.env.STAGE_NAME === 'prod',
});

const container = document.getElementById('root');

const root = createRoot(container!);
root.render(
  <LocationProvider>
    <Crocs path="/" />
  </LocationProvider>,
);
